import React from 'react';

/**
 * Renders a string with any URLs and email addresses converted to clickable
 * links. Admins can paste a URL directly into a description / excerpt / body
 * and it will be linkified automatically — no HTML/Markdown needed.
 *
 * Detection covers:
 *   • http(s)://…
 *   • www.…  (prefixed with https:// on click)
 *   • bare email addresses (mailto:)
 *
 * External http/https links open in a new tab with proper rel attrs.
 */

// Matches URLs and emails as separate whole tokens. The final punctuation
// character(s) commonly trailing a URL in prose (period, comma, close-paren,
// semi-colon, exclamation) is trimmed off before rendering.
const TOKEN_RE = /((?:https?:\/\/|www\.)[^\s<]+|[\w.+-]+@[\w.-]+\.[A-Za-z]{2,})/g;
const TRAIL_PUNCT = /[.,!?;:)\]}>'"]+$/;

const buildHref = (raw) => {
  if (raw.includes('@') && !raw.startsWith('http')) return `mailto:${raw}`;
  if (raw.startsWith('www.')) return `https://${raw}`;
  return raw;
};

const linkify = (text) => {
  if (!text) return null;
  const parts = [];
  let lastIndex = 0;
  let m;
  let idx = 0;
  const re = new RegExp(TOKEN_RE.source, 'g');
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    let raw = m[0];
    // Strip trailing punctuation so it stays outside the anchor.
    let trailing = '';
    const trailMatch = raw.match(TRAIL_PUNCT);
    if (trailMatch) {
      trailing = trailMatch[0];
      raw = raw.slice(0, -trailing.length);
    }
    const href = buildHref(raw);
    const isMail = href.startsWith('mailto:');
    parts.push(
      <a
        key={`lk-${idx++}`}
        href={href}
        target={isMail ? undefined : '_blank'}
        rel={isMail ? undefined : 'noopener noreferrer'}
        className="text-[#3a2359] underline decoration-[#a68a5b] underline-offset-[3px] hover:text-[#5b3a8a] hover:decoration-[#3a2359] break-words"
      >
        {raw}
      </a>
    );
    if (trailing) parts.push(trailing);
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const LinkifiedText = ({ text, as: As = 'span', className = '', ...rest }) => (
  <As className={className} {...rest}>{linkify(String(text || ''))}</As>
);

export default LinkifiedText;
